import type { RuntimeViewState } from "./runtime-state.js";

export type AnalyticsEventName =
  | "landing_play_click"
  | "game_connected"
  | "first_stone_collected"
  | "level_reached_10"
  | "session_ended";

export type AnalyticsEventProperties = Record<
  string,
  string | number | boolean | null
>;

export interface AnalyticsConfig {
  endpoint: string | null;
  debug: boolean;
}

export interface AnalyticsWindow {
  plausible?: (
    eventName: string,
    options?: { props?: AnalyticsEventProperties },
  ) => void;
  posthog?: {
    capture: (eventName: string, properties?: AnalyticsEventProperties) => void;
  };
  gtag?: (
    command: "event",
    eventName: string,
    properties?: AnalyticsEventProperties,
  ) => void;
}

interface AnalyticsSessionState {
  connected: boolean;
  firstStoneCollected: boolean;
  levelReached10: boolean;
  startedAt: number | null;
  maxLevel: number;
  maxScore: number;
  stonePickups: number;
}

export interface AnalyticsTracker {
  track: (
    eventName: AnalyticsEventName,
    properties?: AnalyticsEventProperties,
  ) => void;
  flushSessionEnd: (reason: string, state?: RuntimeViewState | null) => void;
  installRuntimeTracking: (
    subscribe: (listener: (state: RuntimeViewState) => void) => () => void,
  ) => () => void;
}

export function createAnalyticsTracker(
  config: AnalyticsConfig,
  targetWindow: (Window & AnalyticsWindow) | null = readWindow(),
  now: () => number = () => Date.now(),
): AnalyticsTracker {
  const session: AnalyticsSessionState = {
    connected: false,
    firstStoneCollected: false,
    levelReached10: false,
    startedAt: null,
    maxLevel: 1,
    maxScore: 0,
    stonePickups: 0,
  };
  let sessionEnded = false;

  function track(
    eventName: AnalyticsEventName,
    properties: AnalyticsEventProperties = {},
  ): void {
    const payload = sanitizeProperties({
      ...properties,
      locale: readDocumentLanguage(targetWindow),
      path: targetWindow?.location?.pathname ?? null,
    });

    targetWindow?.plausible?.(eventName, { props: payload });
    targetWindow?.posthog?.capture(eventName, payload);
    targetWindow?.gtag?.("event", eventName, payload);
    sendEndpointEvent(config.endpoint, eventName, payload, targetWindow);

    if (config.debug) {
      console.info("[analytics]", eventName, payload);
    }
  }

  function flushSessionEnd(
    reason: string,
    state: RuntimeViewState | null = null,
  ): void {
    if (sessionEnded || session.startedAt === null) {
      return;
    }
    sessionEnded = true;
    updateSessionFromState(session, state);
    track("session_ended", {
      reason,
      durationMs: Math.max(0, now() - session.startedAt),
      maxLevel: session.maxLevel,
      maxScore: session.maxScore,
      stonePickups: session.stonePickups,
      connected: session.connected,
    });
  }

  function installRuntimeTracking(
    subscribe: (listener: (state: RuntimeViewState) => void) => () => void,
  ): () => void {
    let previousPickupEffectCount = 0;
    return subscribe((state) => {
      updateSessionFromState(session, state);
      if (!session.connected && state.welcome) {
        session.connected = true;
        session.startedAt = now();
        track("game_connected", {
          roomId: state.welcome.roomId,
          tickRate: state.welcome.tickRate,
        });
      }

      const selfId = state.welcome?.playerId ?? state.lastSnapshot?.self.id;
      const newPickupEffects = state.pickupEffects.slice(
        0,
        Math.max(0, state.pickupEffects.length - previousPickupEffectCount),
      );
      previousPickupEffectCount = state.pickupEffects.length;
      const selfPickupCount = selfId
        ? newPickupEffects.filter((effect) => effect.playerId === selfId).length
        : 0;
      if (selfPickupCount > 0) {
        session.stonePickups += selfPickupCount;
      }
      if (!session.firstStoneCollected && selfPickupCount > 0) {
        session.firstStoneCollected = true;
        track("first_stone_collected", {
          level: session.maxLevel,
          score: session.maxScore,
        });
      }

      if (!session.levelReached10 && session.maxLevel >= 10) {
        session.levelReached10 = true;
        track("level_reached_10", {
          score: session.maxScore,
          stonePickups: session.stonePickups,
        });
      }
    });
  }

  return {
    track,
    flushSessionEnd,
    installRuntimeTracking,
  };
}

export function readAnalyticsConfigFromEnv(env: {
  readonly VITE_ANALYTICS_ENDPOINT?: string | boolean;
  readonly VITE_ANALYTICS_DEBUG?: string | boolean;
}): AnalyticsConfig {
  return {
    endpoint: readNonEmpty(env.VITE_ANALYTICS_ENDPOINT),
    debug: parseBoolean(env.VITE_ANALYTICS_DEBUG),
  };
}

function updateSessionFromState(
  session: AnalyticsSessionState,
  state: RuntimeViewState | null,
): void {
  if (!state?.lastSnapshot) {
    return;
  }
  session.maxLevel = Math.max(session.maxLevel, state.lastSnapshot.self.level);
  const selfId = state.welcome?.playerId ?? state.lastSnapshot.self.id;
  const selfLeaderboardEntry = state.lastLeaderboard.find(
    (entry) => entry.id === selfId,
  );
  session.maxScore = Math.max(
    session.maxScore,
    selfLeaderboardEntry?.score ?? 0,
    state.lastSettledScore?.score ?? 0,
  );
}

function sendEndpointEvent(
  endpoint: string | null,
  eventName: AnalyticsEventName,
  properties: AnalyticsEventProperties,
  targetWindow: Window | null,
): void {
  if (!endpoint || !targetWindow) {
    return;
  }
  const body = JSON.stringify({
    event: eventName,
    properties,
    timestamp: new Date().toISOString(),
  });
  if (typeof targetWindow.navigator.sendBeacon === "function") {
    targetWindow.navigator.sendBeacon(
      endpoint,
      new Blob([body], { type: "application/json" }),
    );
    return;
  }
  void targetWindow.fetch(endpoint, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  });
}

function sanitizeProperties(
  properties: AnalyticsEventProperties,
): AnalyticsEventProperties {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  );
}

function readDocumentLanguage(targetWindow: Window | null): string | null {
  return targetWindow?.document?.documentElement.lang || null;
}

function readNonEmpty(value: string | boolean | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

function parseBoolean(value: string | boolean | undefined): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  return value === "1" || value === "true";
}

function readWindow(): (Window & AnalyticsWindow) | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window as Window & AnalyticsWindow;
}
